import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/context/AuthContext';
import { SocketProvider } from './components/context/SocketContext';
import { UserProvider } from './components/context/UserContext';
import { Toaster } from 'react-hot-toast';
import Clients from './components/admin/Clients';
import DeleteBets from './components/admin/DeleteBets';
import ManualSettlement from './components/admin/ManualSettlement';
import MatchLockManager from './components/admin/MatchLockManager';

// Common Components
import Login from './components/common/Login';
import Unauthorized from './components/common/Unauthorized';
import Layout from './components/common/Layout'; // Only for admin

// Admin Components
import AdminDashboard from './components/admin/AdminDashboard.js';
import UserList from './components/admin/UserList';
import CreateUser from './components/admin/CreateUser';
import UserDetails from './components/admin/UserDetails';
import CreditDebit from './components/admin/CreditDebit';
import DownlineManagement from './components/admin/DownlineManagement';
import AdminAccountStatement from './components/admin/AccountStatement';
import AdminCurrentBets from './components/admin/CurrentBets';
import AdminChangePassword from './components/admin/ChangePassword';

// User Components (these have their own layout)
import UserDashboard from './components/user/UserDashboard';
import UserAccountStatement from './components/user/AccountStatement';
import UserCurrentBets from './components/user/CurrentBets';
import UserChangePassword from './components/user/ChangePassword';
import ResponsibleGaming from './components/user/ResponsibleGaming';
import TermsAndConditions from './components/user/TermsAndConditions';

// Cricket Components (for users)
import CricketDashboard from './components/cricket/CricketDashboard.js';
import UnifiedMatchDetails from './components/cricket/UnifiedMatchDetails.js';

// Global Styles
import './styles/common/App.css';

const PrivateRoute = ({ children, allowedRoles = [] }) => {
    const { isAuthenticated, user, loading } = useAuth();
    
    if (loading) {
        return <div className="loading">Loading...</div>;
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/login" />;
    }
    
    if (allowedRoles.length > 0) {
        if (!allowedRoles.includes(user?.role)) {
            if (user?.role === 'user') {
                return <Navigate to="/user/dashboard" />;
            } else {
                return <Navigate to="/admin/dashboard" />;
            }
        }
    }
    
    return children;
};

function App() {
    return (
        <AuthProvider>
            <Toaster 
                position="top-right"
                reverseOrder={false}
                gutter={8}
                toastOptions={{
                    duration: 3000,
                    style: {
                        background: '#363636',
                        color: '#fff',
                    },
                    success: {
                        duration: 3000,
                        iconTheme: {
                            primary: '#10b981',
                            secondary: '#fff',
                        },
                    },
                    error: {
                        duration: 4000,
                        iconTheme: {
                            primary: '#ef4444',
                            secondary: '#fff',
                        },
                    },
                }}
            />
            <SocketProvider>
                <UserProvider>
                    <Router>
                        <Routes>
                            {/* Public Routes */}
                            <Route path="/login" element={<Login />} />
                            <Route path="/unauthorized" element={<Unauthorized />} />
                            
                            {/* Admin Routes - with Layout (admin header/sidebar) */}
                            {/* Admin Routes */}
<Route path="/admin" element={
    <PrivateRoute allowedRoles={['admin', 'super_master', 'master', 'agent']}>
        <Layout />
    </PrivateRoute>
}>
    
    <Route index element={<Navigate to="/admin/dashboard" />} />
    <Route path="dashboard" element={<AdminDashboard />} />
    <Route path="clients" element={<Clients />} />  {/* Keep only this one */}
    <Route path="clients/create" element={<CreateUser />} />
    <Route path="clients/:userId" element={<UserDetails />} />
    <Route path="change-password" element={<AdminChangePassword />} />
    <Route path="wallet/:userId" element={<CreditDebit />} />
    <Route path="downline/:userId" element={<DownlineManagement />} />
    <Route path="account-statement" element={<AdminAccountStatement />} />
    <Route path="current-bets" element={<AdminCurrentBets />} />
    <Route path="change-password" element={<AdminChangePassword />} />
    <Route path="delete-bets" element={<DeleteBets />} />
    <Route path="settlement" element={<ManualSettlement />} />
    <Route path="match-lock" element={<MatchLockManager />} /> 
</Route>
                            
                            {/* User Routes - NO Layout, direct to user components */}
                            <Route path="/user/dashboard" element={
                                <PrivateRoute allowedRoles={['user']}>
                                    <UserDashboard />
                                </PrivateRoute>
                            } />
                            
                            <Route path="/user/account-statement" element={
                                <PrivateRoute allowedRoles={['user']}>
                                    <UserAccountStatement />
                                </PrivateRoute>
                            } />
                            
                            <Route path="/user/current-bets" element={
                                <PrivateRoute allowedRoles={['user']}>
                                    <UserCurrentBets />
                                </PrivateRoute>
                            } />
                            
                            <Route path="/user/change-password" element={
                                <PrivateRoute allowedRoles={['user']}>
                                    <UserChangePassword />
                                </PrivateRoute>
                            } />
                            
                            <Route path="/user/cricket" element={
                                <PrivateRoute allowedRoles={['user']}>
                                    <CricketDashboard />
                                </PrivateRoute>
                            } />
                            
                            <Route path="/user/match/:marketId" element={
                                <PrivateRoute allowedRoles={['user']}>
                                    <UnifiedMatchDetails />
                                </PrivateRoute>
                            } />

                            <Route
                                path="/responsible-gaming"
                                element={
                                 
                                        <ResponsibleGaming />
                                
                                }
                            />

                            <Route
                                path="/terms-and-conditions"
                                element={
                                   
                                        <TermsAndConditions />
                                    
                                }
                            />
                            
                            {/* Legacy Routes - Redirect to new structure */}
                            <Route path="/dashboard" element={<Navigate to="/admin/dashboard" />} />
                            <Route path="/users" element={<Navigate to="/admin/clients" />} />
                            <Route path="/users/create" element={<Navigate to="/admin/clients/create" />} />
                            <Route path="/users/:userId" element={<Navigate to="/admin/clients/:userId" />} />
                            <Route path="/wallet/:userId" element={<Navigate to="/admin/wallet/:userId" />} />
                            <Route path="/downline/:userId" element={<Navigate to="/admin/downline/:userId" />} />
                            <Route path="/account-statement" element={<Navigate to="/admin/account-statement" />} />
                            <Route path="/current-bets" element={<Navigate to="/admin/current-bets" />} />
                            <Route path="/change-password" element={<Navigate to="/admin/change-password" />} />
                            <Route path="/cricket" element={<Navigate to="/user/cricket" />} />
                            <Route path="/match/:marketId" element={<Navigate to="/user/match/:marketId" />} />
                            
                            {/* Catch all */}
                            <Route path="*" element={<Navigate to="/login" />} />
                        </Routes>
                    </Router>
                </UserProvider>
            </SocketProvider>
        </AuthProvider>
    );
}

export default App;